import { useMemo, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';

/**
 * Form de configuración de la RULETA (clon de fede-ruleta.png).
 *
 * El admin define los segmentos (color, label, emoji, tipo de premio, monto,
 * peso %), la duración del evento y ve una vista previa de la rueda. Al guardar,
 * arma `config.segments[]` + `wagerMultiplier` y llama `eventsApi.create`
 * (vía `useCreateEvent`) con `type: 'ruleta'`.
 *
 * El orquestador monta este componente dentro del tab "Ruleta" de eventos.tsx.
 */

type PrizeKind = 'fichas' | 'bono100' | 'bono200' | 'free_spin' | 'none';

interface Segment {
  label: string;
  emoji: string;
  color: string;
  prizeKind: PrizeKind;
  amount: number;
  weight: number;
}

const PRIZE_KIND_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'fichas', label: 'Fichas' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'bono200', label: 'Bono 200%' },
  { value: 'free_spin', label: 'Gira gratis' },
  { value: 'none', label: 'Sin premio' },
];

/** Defaults espejo del screenshot fede-ruleta.png (8 segmentos, suman 100%). */
const DEFAULT_SEGMENTS: Segment[] = [
  { label: 'JACKPOT', emoji: '💎', color: '#FFD700', prizeKind: 'fichas', amount: 50000, weight: 2 },
  { label: '10.000 Fichas', emoji: '🔥', color: '#e74c3c', prizeKind: 'fichas', amount: 10000, weight: 8 },
  { label: '5.000 Fichas', emoji: '⭐', color: '#3498db', prizeKind: 'fichas', amount: 5000, weight: 12 },
  { label: '2.000 Fichas', emoji: '🍀', color: '#2ecc71', prizeKind: 'fichas', amount: 2000, weight: 18 },
  { label: '1.000 Fichas', emoji: '🎲', color: '#9b59b6', prizeKind: 'fichas', amount: 1000, weight: 20 },
  { label: '500 Fichas', emoji: '🎁', color: '#e67e22', prizeKind: 'fichas', amount: 500, weight: 15 },
  { label: 'Gira Gratis', emoji: '🔄', color: '#1abc9c', prizeKind: 'free_spin', amount: 0, weight: 10 },
  { label: 'Sin Premio', emoji: '😅', color: '#7f8c8d', prizeKind: 'none', amount: 0, weight: 15 },
];

const EMOJI_PALETTE = ['💎', '🔥', '⭐', '🍀', '🎲', '🎁', '🔄', '😅', '👑', '🪙', '🎰', '🍒'];

interface RuletaFormProps {
  /** Callback opcional tras crear el evento (cerrar dialog, etc.). */
  onCreated?: () => void;
}

export function RuletaForm({ onCreated }: RuletaFormProps) {
  const [name, setName] = useState('');
  const [durationMin, setDurationMin] = useState(15);
  const [segments, setSegments] = useState<Segment[]>(DEFAULT_SEGMENTS);
  const createMutation = useCreateEvent();

  const total = useMemo(
    () => segments.reduce((acc, s) => acc + (Number(s.weight) || 0), 0),
    [segments],
  );
  const totalOk = total === 100;

  const updateSeg = <K extends keyof Segment>(idx: number, key: K, value: Segment[K]) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };

  const addSegment = () => {
    setSegments((prev) => [
      ...prev,
      {
        label: 'Nuevo premio',
        emoji: EMOJI_PALETTE[prev.length % EMOJI_PALETTE.length],
        color: '#888888',
        prizeKind: 'fichas',
        amount: 0,
        weight: 0,
      },
    ]);
  };

  const removeSegment = (idx: number) => {
    setSegments((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = name.trim().length > 0 && segments.length >= 2 && totalOk;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await createMutation.mutateAsync({
      type: 'ruleta',
      name: name.trim(),
      durationMin,
      status: 'draft',
      config: {
        segments: segments.map((s) => ({
          label: s.label,
          emoji: s.emoji,
          color: s.color,
          prizeKind: s.prizeKind,
          amount: Number(s.amount) || 0,
          weight: Number(s.weight) || 0,
        })),
        wagerMultiplier: 1,
      },
    });
    setName('');
    setSegments(DEFAULT_SEGMENTS);
    setDurationMin(15);
    onCreated?.();
  };

  return (
    <div className="space-y-5">
      {/* Nombre */}
      <div className="space-y-1.5">
        <Label>
          Nombre <span className="text-red-500">*</span>
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="ej: Ruleta Viernes Loco"
        />
      </div>

      {/* Segmentos */}
      <div className="space-y-2">
        <Label>Segmentos de la ruleta (probabilidad total = 100%)</Label>
        <div className="space-y-2">
          {segments.map((seg, idx) => (
            <div
              key={idx}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 p-2"
            >
              {/* Color swatch + hex */}
              <input
                type="color"
                value={seg.color}
                onChange={(e) => updateSeg(idx, 'color', e.currentTarget.value)}
                className="h-9 w-9 shrink-0 cursor-pointer rounded border border-input bg-transparent p-0.5"
                title="Color del segmento"
              />
              <Input
                value={seg.color}
                onChange={(e) => updateSeg(idx, 'color', e.currentTarget.value)}
                className="w-[78px] font-mono text-base md:text-xs"
                aria-label="Hex color"
              />

              {/* Label */}
              <Input
                value={seg.label}
                onChange={(e) => updateSeg(idx, 'label', e.currentTarget.value)}
                className="min-w-[140px] flex-1"
                placeholder="Nombre del premio"
              />

              {/* Emoji */}
              <select
                value={seg.emoji}
                onChange={(e) => updateSeg(idx, 'emoji', e.currentTarget.value)}
                className="h-9 w-12 rounded-md border border-input bg-background text-center text-lg"
                aria-label="Emoji"
              >
                {(EMOJI_PALETTE.includes(seg.emoji)
                  ? EMOJI_PALETTE
                  : [seg.emoji, ...EMOJI_PALETTE]
                ).map((em) => (
                  <option key={em} value={em}>
                    {em}
                  </option>
                ))}
              </select>

              {/* Tipo de premio */}
              <Select
                value={seg.prizeKind}
                onValueChange={(v) => updateSeg(idx, 'prizeKind', v as PrizeKind)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIZE_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Monto $ */}
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  type="number"
                  min="0"
                  value={seg.amount}
                  onChange={(e) => updateSeg(idx, 'amount', Number(e.currentTarget.value))}
                  className="w-[92px]"
                  disabled={seg.prizeKind === 'none' || seg.prizeKind === 'free_spin'}
                />
              </div>

              {/* Peso % */}
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={seg.weight}
                  onChange={(e) => updateSeg(idx, 'weight', Number(e.currentTarget.value))}
                  className="w-[64px]"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>

              {/* Eliminar */}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-red-500"
                onClick={() => removeSegment(idx)}
                disabled={segments.length <= 2}
                title="Eliminar segmento"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" size="sm" onClick={addSegment}>
            <Plus className="mr-1.5 h-4 w-4" />
            Agregar segmento
          </Button>
          <span
            className={`text-sm font-semibold ${totalOk ? 'text-green-500' : 'text-amber-500'}`}
          >
            Total: {total}%
          </span>
        </div>
      </div>

      {/* Duración */}
      <div className="space-y-1.5">
        <Label>
          Duración (minutos) <span className="text-red-500">*</span>
        </Label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="1"
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e.currentTarget.value))}
            className="w-32"
          />
          <span className="text-xs text-muted-foreground">
            La ruleta se cierra automáticamente después de este tiempo
          </span>
        </div>
      </div>

      {/* Vista previa */}
      <div className="flex flex-col items-center gap-2 pt-2">
        <span className="text-sm text-muted-foreground">Vista previa:</span>
        <RuletaPreview segments={segments} />
      </div>

      {/* Guardar */}
      <div className="flex justify-end pt-2">
        <Button onClick={handleSubmit} disabled={!canSubmit || createMutation.isPending}>
          {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear ruleta
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Vista previa de la rueda (SVG, igual proporción que el widget)      */
/* ------------------------------------------------------------------ */

function polar(angleDeg: number, r: number): [number, number] {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return [50 + r * Math.cos(rad), 50 + r * Math.sin(rad)];
}

function RuletaPreview({ segments }: { segments: Segment[] }) {
  const n = Math.max(segments.length, 1);
  const slice = 360 / n;

  return (
    <svg viewBox="0 0 100 100" width={170} height={170} className="drop-shadow">
      {segments.map((s, i) => {
        const start = i * slice;
        const end = (i + 1) * slice;
        const [x1, y1] = polar(start, 48);
        const [x2, y2] = polar(end, 48);
        const largeArc = slice > 180 ? 1 : 0;
        const d = `M50,50 L${x1.toFixed(2)},${y1.toFixed(2)} A48,48 0 ${largeArc} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
        const [lx, ly] = polar((start + end) / 2, 30);
        return (
          <g key={i}>
            <path d={d} fill={s.color} stroke="#0b1220" strokeWidth={0.5} />
            <text
              x={lx}
              y={ly}
              fontSize={6}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {s.emoji}
            </text>
          </g>
        );
      })}
      <circle cx={50} cy={50} r={6} fill="#0b1220" stroke="#FFD700" strokeWidth={1} />
    </svg>
  );
}
